import { convertLrc } from '../core/converter';
import { detectLrcFormat } from '../core/detector';
import { extractSongTitle } from '../core/parser';
import type { ConversionMode, HistorySnapshot } from '../core/types';
import { SAMPLE_1, SAMPLE_2 } from '../data/samples';
import { copyOutput, pasteFromClipboard, shareOutput } from './clipboard';
import { escapeHtml, getRequiredElement } from './dom';
import { createLrcEditor, getEditorValue, setEditorValue } from './editor';
import type { LrcEditor } from './editor';
import { initFileHandlers } from './fileHandler';
import { initFormatMenu } from './formatMenu';
import { triggerHaptic } from './haptics';
import { initMetadataModal, syncMetadataBadge } from './metadataModal';
import { getActiveBlankSymbol, initSymbolHandlers, onSymbolChange } from './symbols';
import { hasHistorySnapshot, registerUndoHandler, setHistorySnapshot, showToast, triggerUndo } from './toast';

let editor: LrcEditor | null = null;
let currentFontSizeIndex = 1; // 0: sm, 1: md, 2: lg
const fontSizes: Array<'sm' | 'md' | 'lg'> = ['sm', 'md', 'lg'];
const fontSizeLabels = ['A-', 'A±', 'A+'];
let isWordWrapEnabled = false;

/**
 * Updates real-time cursor line/col coordinates and detected song offset in the telemetry strip.
 */
export function updateCursorTelemetry(row: number = 1, col: number = 1): void {
  const coordsEl = document.getElementById('editorCursorPosition');
  const offsetEl = document.getElementById('editorOffsetDisplay');

  if (coordsEl) {
    coordsEl.textContent = `Row ${row}, Col ${col}`;
  }

  if (offsetEl) {
    const text = getEditorValue();
    const offsetMatch = text.match(/\[offset:\s*([+-]?\d+)\]/i);
    if (offsetMatch && offsetMatch[1]) {
      const offsetVal = parseInt(offsetMatch[1], 10);
      const sign = offsetVal >= 0 ? '+' : '';
      offsetEl.textContent = `Offset: ${sign}${offsetVal}ms`;
      offsetEl.style.color = '#38bdf8';
    } else {
      offsetEl.textContent = 'Offset: +0ms';
      offsetEl.style.color = '';
    }
  }
}

/**
 * Updates the compact mobile accordion badge with current symbol choice.
 */
export function updateOptionsSummary(): void {
  const badge = document.getElementById('optionsSummaryBadge');
  if (!badge) return;

  const symbol = getActiveBlankSymbol();
  badge.textContent = symbol ? `Symbol: ${symbol}` : 'Default (Blank)';
}

/**
 * Evaluates line counts, heuristic format detection, recommended conversion action,
 * and updates badges and telemetry state.
 */
export function updateAdaptiveState(): void {
  const inVal = getEditorValue();
  const inCounter = document.getElementById('lineCounter') || document.getElementById('inputCounter');
  const formatBadge = document.getElementById('formatBadge') as HTMLSpanElement | null;
  const mergeBtn = getRequiredElement<HTMLButtonElement>('mergeBtn');
  const unmergeBtn = getRequiredElement<HTMLButtonElement>('unmergeBtn');
  const undoBtn = document.getElementById('undoBtn') as HTMLButtonElement | null;
  const modeIndicator = document.getElementById('editorModeIndicator');

  const inLines = inVal.trim()
    ? inVal.split(/\r?\n/).filter((l) => l.trim()).length
    : 0;

  if (inCounter) {
    inCounter.textContent = `${inLines} line${inLines === 1 ? '' : 's'}`;
  }

  // Sync metadata tags badge
  syncMetadataBadge();

  // Smart Format Detection
  const detection = detectLrcFormat(inVal);

  if (formatBadge) {
    if (detection.format === 'empty') {
      formatBadge.style.display = 'none';
      formatBadge.textContent = '';
    } else if (detection.format === 'plain') {
      formatBadge.style.display = 'inline-flex';
      formatBadge.className = 'format-badge format-plain';
      formatBadge.innerHTML = '<span class="format-dot"></span><span class="format-label"><span class="badge-desktop-label">Plain Text</span><span class="badge-mobile-label">Plain</span></span>';
    } else if (detection.format === 'merged') {
      formatBadge.style.display = 'inline-flex';
      formatBadge.className = 'format-badge format-merged';
      formatBadge.innerHTML = '<span class="format-dot"></span><span class="format-label"><span class="badge-desktop-label">Multi-Timestamp</span><span class="badge-mobile-label">Merged</span></span>';
    } else if (detection.format === 'unmerged') {
      formatBadge.style.display = 'inline-flex';
      formatBadge.className = 'format-badge format-unmerged';
      formatBadge.innerHTML = '<span class="format-dot"></span><span class="format-label"><span class="badge-desktop-label">Chronological</span><span class="badge-mobile-label">Chrono</span></span>';
    }
  }

  // Dynamic Hero Conversion Button Glow (Intelligent action assignment based on format)
  if (detection.recommendedMode === 'unmerge') {
    unmergeBtn.classList.remove('secondary');
    unmergeBtn.classList.add('hero-glow');
    mergeBtn.classList.add('secondary');
    mergeBtn.classList.remove('hero-glow');
    if (modeIndicator) {
      modeIndicator.textContent = 'Action: Unmerge Ready';
      modeIndicator.style.color = '#c084fc';
      modeIndicator.style.borderColor = 'rgba(168, 85, 247, 0.4)';
    }
  } else {
    mergeBtn.classList.remove('secondary');
    mergeBtn.classList.add('hero-glow');
    unmergeBtn.classList.add('secondary');
    unmergeBtn.classList.remove('hero-glow');
    if (modeIndicator) {
      if (detection.format === 'unmerged') {
        modeIndicator.textContent = 'Action: Merge Ready';
        modeIndicator.style.color = '#38bdf8';
        modeIndicator.style.borderColor = 'rgba(56, 189, 248, 0.4)';
      } else if (detection.format === 'plain') {
        modeIndicator.textContent = 'Plain Lyrics Loaded';
        modeIndicator.style.color = '#fde047';
        modeIndicator.style.borderColor = 'rgba(251, 191, 36, 0.4)';
      } else {
        modeIndicator.textContent = 'Studio Ready';
        modeIndicator.style.color = '';
        modeIndicator.style.borderColor = '';
      }
    }
  }

  // Update undo button disabled state
  if (undoBtn) {
    undoBtn.disabled = !hasHistorySnapshot();
  }
}

/**
 * Backward-compatible alias for updateAdaptiveState.
 */
export const updateCounters = updateAdaptiveState;

/**
 * Runs merge or unmerge conversion pipeline in-place within the unified studio editor.
 */
export function processConversion(mode: ConversionMode): void {
  const statsEl = document.getElementById('statsBadge') || document.getElementById('stats');
  const raw = getEditorValue();

  if (!raw.trim()) {
    showToast('Please paste or load an LRC first.');
    return;
  }

  // Dismiss mobile software keyboard
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  triggerHaptic(15);

  // Save history snapshot for Undo before modifying
  setHistorySnapshot({
    lyrics: raw,
    input: raw,
    statsHtml: statsEl ? statsEl.innerHTML : '',
    statsDisplay: statsEl ? statsEl.style.display : 'none'
  });

  const blankSymbol = getActiveBlankSymbol();

  const result = convertLrc(raw, mode, {
    blankHandling: 'merge',
    blankSymbol,
    normalizeSpaces: true,
    spacing: 'none',
    cleanPunctuation: true
  });

  setEditorValue(result.text);
  updateCounters();

  if (statsEl) {
    statsEl.style.display = 'inline-flex';
    statsEl.innerHTML = `<span class="badge-highlight">${result.inputLines}</span> &rarr; <span class="badge-highlight">${result.outputLines}</span><span class="badge-lines-label">&nbsp;lines</span>`;
  }

  const statusMsg =
    mode === 'unmerge'
      ? 'Unmerged & sorted chronologically!'
      : 'Merged identical lines!';

  showToast(`✓ ${statusMsg}`);
}

/**
 * Loads pre-packaged sample lyrics into the editor.
 */
export function loadSample(num: 1 | 2): void {
  const statsEl = document.getElementById('statsBadge') || document.getElementById('stats');
  const current = getEditorValue();

  if (current.trim()) {
    setHistorySnapshot({
      lyrics: current,
      input: current,
      statsHtml: statsEl ? statsEl.innerHTML : '',
      statsDisplay: statsEl ? statsEl.style.display : 'none'
    });
  }

  setEditorValue(num === 2 ? SAMPLE_2 : SAMPLE_1);
  if (statsEl) statsEl.style.display = 'none';

  updateCounters();
  triggerHaptic(10);
  showToast(`✓ Loaded Sample ${num}`);
}

/**
 * Clears the editor while saving snapshot to the undo history buffer.
 */
export function clearAll(): void {
  const statsEl = document.getElementById('statsBadge') || document.getElementById('stats');
  const current = getEditorValue();

  if (!current.trim()) {
    showToast('Already empty.');
    return;
  }

  triggerHaptic(12);

  setHistorySnapshot({
    lyrics: current,
    input: current,
    statsHtml: statsEl ? statsEl.innerHTML : '',
    statsDisplay: statsEl ? statsEl.style.display : 'none'
  });

  setEditorValue('');
  if (statsEl) statsEl.style.display = 'none';

  updateCounters();
  showToast('Cleared • <span class="undo-action">Undo</span>');
}

/**
 * Restores previous lyrics content from snapshot.
 */
export function restoreSnapshot(snapshot: HistorySnapshot): void {
  const statsEl = document.getElementById('statsBadge') || document.getElementById('stats');

  setEditorValue(snapshot.lyrics ?? snapshot.input ?? '');

  if (statsEl && snapshot.statsHtml && snapshot.statsDisplay && snapshot.statsDisplay !== 'none') {
    statsEl.innerHTML = snapshot.statsHtml;
    statsEl.style.display = snapshot.statsDisplay;
  } else if (statsEl) {
    statsEl.style.display = 'none';
  }

  triggerHaptic(12);
  updateCounters();
  showToast('✓ Lyrics restored!');
}

/**
 * Backward compatibility alias for useOutputAsInput.
 */
export function useOutputAsInput(): void {
  showToast('Lyrics are already active in the Studio Editor.');
}

/**
 * Triggers a browser download of the active editor text as a .lrc file.
 */
export function downloadOutput(): void {
  const content = getEditorValue();

  if (!content.trim()) {
    showToast('Nothing to download yet. Enter or paste lyrics first.');
    return;
  }

  try {
    const title = extractSongTitle(content);
    const safeName = title ? `${title}_converted.lrc` : 'converted.lrc';

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);

    triggerHaptic(12);
    showToast(`✓ Downloaded ${escapeHtml(safeName)}`);
  } catch (err) {
    console.error('Download failed:', err);
    showToast('Download failed, please copy instead.');
  }
}

/**
 * Main application initialization function.
 */
export function initApp(): void {
  const editorRoot = getRequiredElement<HTMLDivElement>('editorRoot');
  editor = createLrcEditor(editorRoot, {
    onChange: () => updateAdaptiveState(),
    onCursorChange: (row, col) => updateCursorTelemetry(row, col)
  });

  const mergeBtn = getRequiredElement<HTMLButtonElement>('mergeBtn');
  const unmergeBtn = getRequiredElement<HTMLButtonElement>('unmergeBtn');
  const sample1Btn = getRequiredElement<HTMLButtonElement>('sample1Btn');
  const sample2Btn = getRequiredElement<HTMLButtonElement>('sample2Btn');
  const clearBtn = getRequiredElement<HTMLButtonElement>('clearBtn');
  const copyBtn = getRequiredElement<HTMLButtonElement>('copyBtn');
  const downloadBtn = getRequiredElement<HTMLButtonElement>('downloadBtn');
  const undoBtn = document.getElementById('undoBtn') as HTMLButtonElement | null;
  const shareBtn = document.getElementById('shareBtn') as HTMLButtonElement | null;
  const pasteBtn = document.getElementById('pasteBtn') as HTMLButtonElement | null;

  // Conversion actions
  mergeBtn.addEventListener('click', () => processConversion('merge'));
  unmergeBtn.addEventListener('click', () => processConversion('unmerge'));

  // Undo button action
  if (undoBtn) {
    undoBtn.addEventListener('click', () => {
      if (hasHistorySnapshot()) {
        triggerHaptic(10);
        triggerUndo();
      }
    });
  }

  // Header and toolbar samples dropdown
  const samplesDropdown = document.getElementById('samplesDropdown') as HTMLDivElement | null;
  const samplesToggleBtn = document.getElementById('samplesToggleBtn') as HTMLButtonElement | null;
  const samplesMenu = document.getElementById('samplesMenu') as HTMLDivElement | null;

  const closeSamplesDropdown = () => {
    if (samplesMenu && samplesToggleBtn) {
      samplesMenu.classList.remove('show');
      samplesToggleBtn.setAttribute('aria-expanded', 'false');
    }
  };

  if (samplesDropdown && samplesToggleBtn && samplesMenu) {
    samplesToggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = samplesMenu.classList.contains('show');
      if (isOpen) {
        closeSamplesDropdown();
      } else {
        samplesMenu.classList.add('show');
        samplesToggleBtn.setAttribute('aria-expanded', 'true');
      }
    });

    document.addEventListener('click', (e) => {
      if (!samplesDropdown.contains(e.target as Node)) {
        closeSamplesDropdown();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeSamplesDropdown();
      }
    });
  }

  sample1Btn.addEventListener('click', () => {
    loadSample(1);
    closeSamplesDropdown();
  });
  sample2Btn.addEventListener('click', () => {
    loadSample(2);
    closeSamplesDropdown();
  });
  clearBtn.addEventListener('click', clearAll);

  // 1-Tap Paste Button
  if (pasteBtn) {
    pasteBtn.addEventListener('click', async () => {
      const pasted = await pasteFromClipboard();
      if (pasted) {
        const current = getEditorValue();
        if (current.trim()) {
          const statsEl = document.getElementById('statsBadge') || document.getElementById('stats');
          setHistorySnapshot({
            lyrics: current,
            input: current,
            statsHtml: statsEl ? statsEl.innerHTML : '',
            statsDisplay: statsEl ? statsEl.style.display : 'none'
          });
          showToast('✓ Pasted from clipboard! • <span class="undo-action">Undo</span>');
        } else {
          showToast('✓ Pasted from clipboard!');
        }
        setEditorValue(pasted);
        updateCounters();
      }
    });
  }

  // Export actions
  copyBtn.addEventListener('click', copyOutput);
  downloadBtn.addEventListener('click', downloadOutput);

  if (shareBtn && typeof navigator.share === 'function') {
    shareBtn.addEventListener('click', shareOutput);
  }

  // Header quick export button
  const headerExportBtn = document.getElementById('headerExportBtn');
  if (headerExportBtn) {
    headerExportBtn.addEventListener('click', downloadOutput);
  }

  // Quick Editor Tools (Wrap, Font Size)
  const wrapToggleBtn = document.getElementById('wrapToggleBtn');
  const fontSizeBtn = document.getElementById('fontSizeBtn');

  if (wrapToggleBtn) {
    wrapToggleBtn.addEventListener('click', () => {
      isWordWrapEnabled = !isWordWrapEnabled;
      editor?.setWordWrap(isWordWrapEnabled);
      wrapToggleBtn.classList.toggle('active', isWordWrapEnabled);
      triggerHaptic(8);
    });
  }

  if (fontSizeBtn) {
    fontSizeBtn.addEventListener('click', () => {
      currentFontSizeIndex = (currentFontSizeIndex + 1) % fontSizes.length;
      const nextSize = fontSizes[currentFontSizeIndex] ?? 'md';
      editor?.setFontSize(nextSize);
      fontSizeBtn.textContent = fontSizeLabels[currentFontSizeIndex] ?? 'A±';
      triggerHaptic(8);
    });
  }

  // Quick Bracket Tool ([ ] timestamp bracket wrapper/inserter)
  const bracketInsertBtn = document.getElementById('bracketInsertBtn');
  if (bracketInsertBtn) {
    bracketInsertBtn.addEventListener('click', () => {
      triggerHaptic(8);
      editor?.wrapSelection('[', ']');
      updateAdaptiveState();
    });
  }

  // Mobile virtual keyboard awareness:
  // When editor is focused on mobile screens, smoothly tuck floating dock away so it never covers the cursor/line.
  editorRoot.addEventListener('focusin', () => {
    if (window.innerWidth <= 768) {
      document.body.classList.add('mobile-editor-focused');
    }
  });

  editorRoot.addEventListener('focusout', () => {
    document.body.classList.remove('mobile-editor-focused');
  });

  // Global Keyboard shortcuts:
  // - Ctrl/Cmd + Enter: Merge
  // - Ctrl/Cmd + Shift + Enter: Unmerge
  // - Ctrl/Cmd + S: Download .lrc
  // - Ctrl/Cmd + Z: Undo (when history snapshot available)
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    const isCtrl = e.ctrlKey || e.metaKey;
    if (!isCtrl) return;

    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        processConversion('unmerge');
      } else {
        processConversion('merge');
      }
    } else if (e.key === 's' || e.key === 'S') {
      e.preventDefault();
      downloadOutput();
    } else if ((e.key === 'z' || e.key === 'Z') && !e.shiftKey) {
      if (hasHistorySnapshot()) {
        e.preventDefault();
        triggerUndo();
      }
    }
  });

  // Sub-system initializations
  registerUndoHandler(restoreSnapshot);
  initSymbolHandlers();
  initMetadataModal();
  initFormatMenu();
  onSymbolChange(updateOptionsSummary);
  initFileHandlers((content) => {
    const current = getEditorValue();
    if (current.trim()) {
      setHistorySnapshot({
        lyrics: current,
        input: current
      });
    }
    setEditorValue(content);
    updateCounters();
    showToast('✓ File loaded into Studio!');
  });

  // Calculate initial counters and options badge
  updateCounters();
  updateOptionsSummary();
  updateCursorTelemetry(1, 1);
}
